import React from 'react';

import {
  AccountBalance as AccountBalanceIcon,
  Home as HomeIcon,
  ManageAccounts as ManageAccountsIcon,
  MenuBook as MenuBookFilledIcon,
  MonetizationOn as MonetizationIcon,
  Notifications as NotificationsIcon,
  Password as PasswordIcon,
  Person as PersonIcon,
  Phonelink as PhonelinkIcon,
  Wysiwyg as WysiwygIcon,
} from '@mui/icons-material';

import { appRoutes } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';

import {
  BaseLayoutLeftMenu,
  LeftMenuLogo,
  MenuItem,
  ResourcesListItem,
} from 'src/components/common';
import { useScreenInfo } from 'src/hooks/common';
import { useGlobalState } from 'src/hooks/shared';
import { useCurrentUserVerifierStaff } from 'src/hooks/verifierStaff';
import { GetInTouchModal } from 'src/modals/getInTouch';

export const LayoutLeftMenu: React.FC = () => {
  const { verifierStaffBillingAccess } = useCurrentUserVerifierStaff();
  const { leftMenuDrawerState } = useGlobalState();
  const { isMobile } = useScreenInfo();

  const hasAccessToBilling = React.useMemo(
    () => verifierStaffBillingAccess?.verifier.type === 'Commercial',
    [verifierStaffBillingAccess],
  );

  const menuItems: Nullable<MenuItem>[] = React.useMemo(
    () => [
      {
        link: appRoutes.inbox.index,
        title: 'Home',
        icon: HomeIcon,
      },
      {
        link: appRoutes.verifierStaff.index,
        title: 'Staff',
        icon: ManageAccountsIcon,
      },
      {
        link: appRoutes.reports.verificationRequests,
        title: 'Verification Requests',
        icon: WysiwygIcon,
      },
    ],
    [],
  );

  const myAccountItems: Nullable<MenuItem>[] = React.useMemo(
    () => [
      {
        link: appRoutes.account.personalInformation,
        title: 'Personal Information',
        icon: PersonIcon,
      },
      {
        link: appRoutes.account.companyInformation,
        title: 'Company information',
        icon: AccountBalanceIcon,
      },
      hasAccessToBilling
        ? {
            link: appRoutes.account.billing,
            title: 'Billing',
            icon: MonetizationIcon,
          }
        : null,
      ...(isMobile
        ? [
            {
              link: appRoutes.account.verificationPurposes,
              title: 'Verification Purposes',
              icon: MenuBookFilledIcon,
            },
          ]
        : []),
      {
        link: appRoutes.account.notifications,
        title: 'Notifications',
        icon: NotificationsIcon,
      },
      {
        link: appRoutes.account.security,
        title: 'Sign-in & Security',
        icon: PasswordIcon,
      },
      {
        link: appRoutes.account.setupMfa,
        title: 'Multi-factor Authentication',
        icon: PhonelinkIcon,
      },
    ],
    [hasAccessToBilling, isMobile],
  );

  return (
    <React.Fragment>
      <BaseLayoutLeftMenu
        leftMenuDrawerState={leftMenuDrawerState}
        myAccountItemsList={myAccountItems}
        menuItemsList={menuItems}
        rootRoute={appRoutes.inbox.index}
        logoutRoute={appRoutes.auth.logout}
        footer={<ResourcesListItem />}
        leftMenuLogo={<LeftMenuLogo showIcon={!leftMenuDrawerState.isOpened} />}
      />
      <GetInTouchModal />
    </React.Fragment>
  );
};
